import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Button,
    Typography,
    Chip,
    Autocomplete,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TablePagination
} from '@mui/material';
import {
    Add as AddIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';

const GestionCentroCosto = () => {
    const { user } = useUser();
    const { tienePermiso } = useAccesos();
    const server = process.env.REACT_APP_API_ROUTE;
    
    // Estados principales
    const [centros, setCentros] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [areas, setAreas] = useState([]);
    const [procesos, setProcesos] = useState([]);
    const [unidadesNegocio, setUnidadesNegocio] = useState([]);
    
    // Estados de control
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [editData, setEditData] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    
    // Estado del formulario
    const [newCentro, setNewCentro] = useState({
        idUnidadNegocio: null,
        idSucursal: null,
        idArea: null,
        idProceso: null
    });
    
    // Estados de filtros
    const [filtroEmpresa, setFiltroEmpresa] = useState(null);
    const [filtroArea, setFiltroArea] = useState(null);

    useEffect(() => {
        fetchCentros();
        fetchEmpresas();
        fetchSucursales();
        fetchProcesos();
        fetchUnidadesNegocio();
    }, []);

    useEffect(() => {
        if (editingId && editingId !== 'NEW') {
            const centro = centros.find(c => c.id === editingId);
            if (centro) {
                setEditData({ ...centro });
            }
        }
    }, [editingId, centros]);

    const fetchCentros = async () => {
        try {
            const response = await fetch(`${server}/CtbCentroCostoXUnidadNegocios`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });

            if (!response.ok) throw new Error('Error al cargar centros de costo');

            const data = await response.json();
            let centrosData = data.$values || data || [];

            // Limpiar datos: remover propiedades de navegación
            centrosData = centrosData.map(centro => ({
                id: centro.id,
                idUnidadNegocio: centro.idUnidadNegocio,
                idSucursal: centro.idSucursal,
                idProceso: centro.idProceso,
                ingresadoPor: centro.ingresadoPor,
                fechaIngreso: centro.fechaIngreso,
                modificacion: centro.modificacion,
                modificadoPor: centro.modificadoPor,
                fechaModificacion: centro.fechaModificacion
            }));

            console.log('✅ Centros de Costo cargados:', centrosData.length);
            setCentros(centrosData);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', 'No se pudo cargar los centros de costo', 'error');
        }
    };

    const fetchEmpresas = async () => {
        try {
            const response = await fetch(`${server}/MgEmpresas`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const data = await response.json();
            setEmpresas(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar empresas:', error);
        }
    };

    const fetchSucursales = async () => {
        try {
            const response = await fetch(`${server}/MgSucursales`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const data = await response.json();
            setSucursales(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar sucursales:', error);
        }
    };

    const fetchProcesos = async () => {
        try {
            const response = await fetch(`${server}/MgProcesoes`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const data = await response.json();
            setProcesos(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar procesos:', error);
        }
    };

    const fetchUnidadesNegocio = async () => {
        try {
            const response = await fetch(`${server}/CtbUnidadNegocios`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const data = await response.json();
            setUnidadesNegocio(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar unidades de negocio:', error);
        }
    };



    const handleAddNew = async () => {
        try {
            const dataToSend = {
                ...newCentro,
                ingresadoPor: user?.user || 'ADMIN',
                fechaIngreso: new Date().toISOString(),
                modificacion: 1
            };

            const response = await fetch(`${server}/CtbCentroCostoXUnidadNegocios`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user?.token}`
                },
                body: JSON.stringify(dataToSend)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                throw new Error(errorData.message || 'Error al crear');
            }

            await Swal.fire('Éxito', 'Registro creado correctamente', 'success');
            setOpenDialog(false);
            setNewCentro({
                idUnidadNegocio: null,
                idSucursal: null,
                idProceso: null
            });
            fetchCentros();
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleEdit = (centro) => {
        setEditingId(centro.id);
        setEditData({ ...centro });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleSaveEdit = async () => {
        try {
            const dataToSend = {
                ...editData,
                modificadoPor: user?.user || 'ADMIN',
                fechaModificacion: new Date().toISOString()
            };

            const response = await fetch(
                `${server}/CtbCentroCostoXUnidadNegocios/${editData.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user?.token}`
                    },
                    body: JSON.stringify(dataToSend)
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                throw new Error(errorData.message || 'Error al actualizar');
            }

            await Swal.fire('Éxito', 'Registro actualizado correctamente', 'success');
            setEditingId(null);
            setEditData({});
            fetchCentros();
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Está seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(
                    `${server}/CtbCentroCostoXUnidadNegocios/${id}/${user?.user || 'ADMIN'}`,
                    {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${user?.token}` }
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ message: 'Error desconocido' }));
                    throw new Error(errorData.message || 'Error al eliminar');
                }

                await Swal.fire('Eliminado', 'Registro eliminado correctamente', 'success');
                fetchCentros();
            } catch (error) {
                console.error('Error:', error);
                Swal.fire('Error', error.message, 'error');
            }
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const matchesSearch = (centro) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        
        const unidadNegocio = unidadesNegocio.find(u => u.id === centro.idUnidadNegocio);
        const empresa = empresas.find(e => e.idEmpresa === centro.idEmpresa);
        const sucursal = sucursales.find(s => s.idSucursal === centro.idSucursal);
        const proceso = procesos.find(p => p.id === centro.idProceso);
        
        return (
            unidadNegocio?.unidadNegocio?.toLowerCase().includes(searchLower) ||
            empresa?.nombreCompleto?.toLowerCase().includes(searchLower) ||
            sucursal?.nombreSucursal?.toLowerCase().includes(searchLower) ||
            proceso?.descripcion?.toLowerCase().includes(searchLower)
        );
    };

    const matchesFilters = (centro) => {
        // Filtro por Empresa
        if (filtroEmpresa) {
            // Verificar si la sucursal seleccionada pertenece a la empresa filtrada
            const sucursal = sucursales.find(s => s.idSucursal === centro.idSucursal);
            if (sucursal && sucursal.idEmpresa !== filtroEmpresa) return false;
        }
        
        return true;
    };

    const filteredCentros = centros.filter(centro => matchesSearch(centro) && matchesFilters(centro));
    const paginatedCentros = filteredCentros.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getNombreUnidadNegocio = (idUnidad) => {
        return unidadesNegocio.find(u => u.id === idUnidad)?.unidadNegocio || 'N/A';
    };

    const getNombreEmpresa = (idSucursal) => {
        // Obtener empresa desde la sucursal
        const sucursal = sucursales.find(s => s.idSucursal === idSucursal);
        if (!sucursal) return 'N/A';
        const empresa = empresas.find(e => e.idEmpresa === sucursal.idEmpresa);
        return empresa?.nombreCompleto || 'N/A';
    };

    const getNombreSucursal = (idSucursal) => {
        return sucursales.find(s => s.idSucursal === idSucursal)?.nombreSucursal || 'N/A';
    };

    const getNombreProceso = (idProceso) => {
        return procesos.find(p => p.id === idProceso)?.descripcion || 'N/A';
    };

    const renderCentroRow = (centro) => {
        const isEditing = editingId === centro.id;

        return (
            <TableRow key={centro.id} hover>
                <TableCell>
                    {isEditing ? (
                        <Autocomplete
                            size="small"
                            value={unidadesNegocio.find(u => u.id === editData.idUnidadNegocio) || null}
                            onChange={(e, newValue) => setEditData({ ...editData, idUnidadNegocio: newValue?.id || null })}
                            options={unidadesNegocio}
                            getOptionLabel={(option) => option.unidadNegocio || ''}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ minWidth: 200 }}
                        />
                    ) : (
                        getNombreUnidadNegocio(centro.idUnidadNegocio)
                    )}
                </TableCell>

                <TableCell>
                    {getNombreEmpresa(centro.idSucursal)}
                </TableCell>

                <TableCell>
                    {isEditing ? (
                        <Autocomplete
                            size="small"
                            value={sucursales.find(s => s.idSucursal === editData.idSucursal) || null}
                            onChange={(e, newValue) => setEditData({ ...editData, idSucursal: newValue?.idSucursal || null })}
                            options={sucursales}
                            getOptionLabel={(option) => option.nombreSucursal || ''}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ minWidth: 150 }}
                        />
                    ) : (
                        getNombreSucursal(centro.idSucursal)
                    )}
                </TableCell>

                <TableCell>
                    {isEditing ? (
                        <Autocomplete
                            size="small"
                            value={procesos.find(p => p.id === editData.idProceso) || null}
                            onChange={(e, newValue) => setEditData({ ...editData, idProceso: newValue?.id || null })}
                            options={procesos}
                            getOptionLabel={(option) => option.descripcion || ''}
                            renderInput={(params) => <TextField {...params} />}
                            sx={{ minWidth: 150 }}
                        />
                    ) : (
                        getNombreProceso(centro.idProceso)
                    )}
                </TableCell>

                <TableCell align="center">
                    {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Guardar">
                                <IconButton size="small" color="primary" onClick={handleSaveEdit} disabled={!tienePermiso('Editar Centro Costo')}>
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                                <IconButton size="small" color="secondary" onClick={handleCancelEdit}>
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Editar">
                                <IconButton size="small" color="primary" onClick={() => handleEdit(centro)} disabled={!tienePermiso('Editar Centro Costo')}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                                <IconButton size="small" color="error" onClick={() => handleDelete(centro.id)} disabled={!tienePermiso('Eliminar Centro Costo')}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <div className="right_col" role="main">
            <div className="page-title">
                <div className="title_left">
                    <h3>Gestión de Centros de Costo</h3>
                </div>
            </div>

            <div className="clearfix"></div>

            <div className="row">
                <div className="col-md-12 col-sm-12">
                    <div className="x_panel">
                        <div className="x_title">
                            <h2>Centros de Costo</h2>
                            <div className="clearfix"></div>
                        </div>
                        <div className="x_content">
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    placeholder="Buscar..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ minWidth: 250 }}
                                />

                                <Autocomplete
                                    size="small"
                                    value={empresas.find(e => e.idEmpresa === filtroEmpresa) || null}
                                    onChange={(e, newValue) => setFiltroEmpresa(newValue?.idEmpresa || null)}
                                    options={empresas}
                                    getOptionLabel={(option) => option.nombreCompleto || ''}
                                    renderInput={(params) => <TextField {...params} label="Filtrar por Empresa" />}
                                    sx={{ minWidth: 200 }}
                                />

                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setOpenDialog(true)}
                                    sx={{ ml: 'auto' }}
                                    disabled={!tienePermiso('Editar Centro Costo')}
                                >
                                    NUEVO REGISTRO
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unidad de Negocio</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Empresa</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sucursal</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Proceso</TableCell>
                                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedCentros.map(centro => renderCentroRow(centro))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={filteredCentros.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Filas por página:"
                            />

                            {filteredCentros.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography color="textSecondary">
                                        {searchTerm ? 'No se encontraron resultados' : 'No hay registros de centro costo - unidad de negocio'}
                                    </Typography>
                                </Box>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog para nuevo centro */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        <Typography variant="h6">Nuevo Registro Centro Costo - Unidad de Negocio</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                value={unidadesNegocio.find(u => u.id === newCentro.idUnidadNegocio) || null}
                                onChange={(e, newValue) => setNewCentro({ ...newCentro, idUnidadNegocio: newValue?.id || null })}
                                options={unidadesNegocio}
                                getOptionLabel={(option) => option.unidadNegocio || ''}
                                renderInput={(params) => <TextField {...params} label="Unidad de Negocio *" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                size="small"
                                value={sucursales.find(s => s.idSucursal === newCentro.idSucursal) || null}
                                onChange={(e, newValue) => setNewCentro({ ...newCentro, idSucursal: newValue?.idSucursal || null })}
                                options={sucursales}
                                getOptionLabel={(option) => option.nombreSucursal || ''}
                                renderInput={(params) => <TextField {...params} label="Sucursal *" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                size="small"
                                value={procesos.find(p => p.id === newCentro.idProceso) || null}
                                onChange={(e, newValue) => setNewCentro({ ...newCentro, idProceso: newValue?.id || null })}
                                options={procesos}
                                getOptionLabel={(option) => option.descripcion || ''}
                                renderInput={(params) => <TextField {...params} label="Proceso" />}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleAddNew}
                        disabled={!newCentro.idUnidadNegocio || !tienePermiso('Editar Centro Costo')}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default GestionCentroCosto;
